import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { login } from '../services/registrationApi'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function LoginPage() {
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<{ email?: string; password?: string; form?: string }>({})

  const validate = () => {
    const errs: typeof errors = {}
    if (!email.trim()) {
      errs.email = 'Email address is required.'
    } else if (!EMAIL_RE.test(email.trim())) {
      errs.email = 'Please enter a valid email address.'
    }
    if (!password) {
      errs.password = 'Password is required.'
    }
    return errs
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }
    setErrors({})
    setIsLoading(true)

    try {
      const response = await login(email.trim(), password)
      localStorage.setItem('jwt_token', response.jwt_token)
      localStorage.setItem('email_verified', String(response.email_verified))
      localStorage.setItem('customer_email', email.trim())
      navigate('/dashboard')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'unknown_error'
      if (message === 'invalid_credentials') {
        setErrors({ form: 'Incorrect email or password. Please try again.' })
      } else if (message === 'rate_limit_exceeded') {
        setErrors({ form: 'Too many login attempts. Please wait a few minutes and try again.' })
      } else {
        setErrors({ form: 'Something went wrong. Please try again.' })
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-rd-bg font-dmSans relative overflow-hidden">
      {/* Ambient glow */}
      <div
        className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px]"
        style={{ background: 'radial-gradient(ellipse, rgba(74,222,128,0.08) 0%, transparent 70%)' }}
      />

      {/* Nav */}
      <nav className="relative z-10 px-6 py-4 border-b border-white/[0.07]">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-rd-text no-underline hover:opacity-80 transition-opacity"
        >
          <img
            src={`${import.meta.env.BASE_URL}logo-icon.png`}
            alt="ReputeDesk"
            className="w-8 h-8 object-contain"
          />
          <span className="font-sora font-bold text-lg">ReputeDesk</span>
        </Link>
      </nav>

      {/* Content */}
      <div className="relative z-10 flex justify-center px-4 py-10 sm:py-14">
        <div className="w-full max-w-md">

          {/* Heading */}
          <div className="text-center mb-7">
            <h1 className="font-sora font-bold text-2xl sm:text-[1.75rem] text-rd-text mb-2 tracking-tight">
              Welcome back
            </h1>
            <p className="text-rd-muted text-sm">Sign in to your account</p>
          </div>

          {/* Card */}
          <div className="bg-rd-surface border border-white/[0.07] rounded-2xl p-6 sm:p-8">

            {/* Form-level error */}
            {errors.form && (
              <div
                role="alert"
                className="mb-5 rounded-lg bg-danger-500/10 border border-danger-500/30 px-4 py-3 text-sm text-danger-500"
              >
                {errors.form}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate className="space-y-5">

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-rd-muted mb-1.5">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-required="true"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  className={`reg-input${errors.email ? ' !border-danger-500/60' : ''}`}
                  placeholder="jane@example.com"
                />
                {errors.email && (
                  <p id="email-error" role="alert" className="mt-1.5 text-sm text-danger-500">
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Password */}
              <div>
                <div className="flex items-baseline justify-between mb-1.5">
                  <label htmlFor="password" className="block text-sm font-medium text-rd-muted">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-rd-accent hover:opacity-80 transition-opacity"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    aria-required="true"
                    aria-invalid={!!errors.password}
                    aria-describedby={errors.password ? 'password-error' : undefined}
                    className={`reg-input pr-12${errors.password ? ' !border-danger-500/60' : ''}`}
                    placeholder="Your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-rd-dim hover:text-rd-muted transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p id="password-error" role="alert" className="mt-1.5 text-sm text-danger-500">
                    {errors.password}
                  </p>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                aria-busy={isLoading}
                className="reg-btn-primary mt-1"
              >
                {isLoading ? (
                  <>
                    <svg aria-hidden="true" className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Signing in…
                  </>
                ) : (
                  'Sign In'
                )}
              </button>

            </form>
          </div>

          {/* Footer link */}
          <p className="text-center text-sm text-rd-muted mt-6">
            Don't have an account?{' '}
            <Link to="/register" className="text-rd-accent hover:opacity-80 transition-opacity">
              Get started free
            </Link>
          </p>

        </div>
      </div>
    </div>
  )
}
